/**
 * EmploymentChanges — "Ndryshimet e punësimit" (ish "Avancimet")
 * Route: /advancements
 *
 * Një kërkesë mund të përmbajë një OSE disa ndryshime (pagë, pozitë, departament,
 * menaxher, lokacion, orar, lloj kontrate, tjetër). Formular dinamik për krijim;
 * tabelë vetëm për listën; panel detajesh me workflow + histori.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Stack, Button, Chip, Avatar, Paper, Grid, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Dialog, DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, Autocomplete, IconButton, Tooltip, Alert, Skeleton, Snackbar, InputAdornment,
  Card, CardActionArea, LinearProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import PlayCircleRoundedIcon from '@mui/icons-material/PlayCircleRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import PageHeader from '../common/PageHeader';
import apiService, { authFetch } from '../../services/apiServices';
import globals from '../../services/globals';
import {
  POSITION_SAME_MESSAGE, isSamePosition, currentPositionOf, selectablePositions,
} from './positionChange';
import { salaryFieldError, currentSalaryOf } from './salaryChange';

/* ─── config ──────────────────────────────────────────────────────────── */
const STATUS = {
  pending:            { label: 'Në pritje',              color: '#B45309', bg: '#FEF3C7' },
  pending_supervisor: { label: 'Në pritje — Mbikëqyrësi', color: '#B45309', bg: '#FEF3C7' },
  pending_hr:         { label: 'Në pritje — HR',          color: '#A16207', bg: '#FEF9C3' },
  // I njëjti ton qelibar si dy pritjet e tjera: një pritje mbetet pritje, kushdo
  // qoftë vendimmarrësi. Vjollca e mëparshme e nxirrte këtë hap nga familja e vet
  // pa shtuar asnjë kuptim.
  pending_management: { label: 'Në pritje — Menaxhmenti', color: '#92400E', bg: '#FEF3C7' },
  approved:     { label: 'Aprovuar',        color: '#0369A1', bg: '#E0F2FE' },
  // "Planifikuar" = aprovuar dhe pret datën. Rri pranë `approved` (kaltër
  // informative), jo në një ngjyrë të vetën.
  scheduled:    { label: 'Planifikuar',     color: '#0369A1', bg: '#F0F9FF' },
  applied:      { label: 'Zbatuar',         color: '#15803D', bg: '#DCFCE7' },
  rejected:     { label: 'Refuzuar',        color: '#B91C1C', bg: '#FEE2E2' },
  cancelled:    { label: 'Anuluar',         color: '#6B7280', bg: '#F3F4F6' },
  needs_review: { label: 'Kërkon rishikim', color: '#C2410C', bg: '#FFEDD5' },
};

// Historiku i veprimeve: fjalë, jo kode. Kodi mbetet si rikthim për veprime të reja.
export const ACTION_LABELS = {
  SUBMIT: 'Parashtruar',
  APPROVE_SUPERVISOR: 'Aprovuar nga mbikëqyrësi',
  APPROVE_HR: 'Aprovuar nga HR',
  APPROVE_MANAGEMENT: 'Aprovuar nga menaxhmenti',
  SKIP_HR: 'HR anashkaluar (punonjës C-Level, ndryshim page)',
  REJECT: 'Refuzuar',
  CANCEL: 'Anuluar',
  SCHEDULE: 'Planifikuar',
  APPLY: 'Zbatuar',
  NEEDS_REVIEW: 'Kërkon rishikim',
};

const CHANGE_TYPES = [
  // No curCmp: salary is compared numerically at the stored scale via
  // salaryFieldError(), not by the generic string equality — "1200.00" against
  // a stored 1200 is the same money.
  { key: 'salary',        label: 'Paga',              icon: PaymentsRoundedIcon,          input: 'number',   unit: '€', manageOnly: true, cur: (w) => `${w?.BaseSalary || 0} €` },
  // No curCmp: position is compared by ID via isSamePosition(), not by the
  // generic name equality the other free-text fields use.
  { key: 'position',      label: 'Pozita',            icon: WorkRoundedIcon,              input: 'position',            cur: (w) => w?.Position || '—' },
  { key: 'department',    label: 'Departamenti',      icon: ApartmentRoundedIcon,         input: 'dept',                cur: (w) => w?.DepartmentName || '—' },
  { key: 'manager',       label: 'Menaxheri',         icon: SupervisorAccountRoundedIcon, input: 'manager',             cur: (w) => w?.SupervisorName || '—' },
  { key: 'location',      label: 'Lokacioni',         icon: PlaceRoundedIcon,             input: 'branch',              cur: (w) => w?.BranchName || '—' },
  { key: 'schedule',      label: 'Orari i punës',     icon: ScheduleRoundedIcon,          input: 'text',                cur: () => '—' },
  { key: 'contract_type', label: 'Lloji i kontratës', icon: DescriptionRoundedIcon,       input: 'worktype',            cur: (w) => (w?.WorkType ? String(w.WorkType) : '—') },
  { key: 'other',         label: 'Tjetër',            icon: MoreHorizRoundedIcon,         input: 'other',               cur: () => '—' },
];
const typeOf = (key) => CHANGE_TYPES.find(c => c.key === key);

const nameOfWorker = (w) => w?.FullName || `${w?.FirstName || ''} ${w?.LastName || ''}`.trim() || '—';
const fmtDT = (v) => {
  if (!v) return '—';
  const d = new Date(v); if (isNaN(d.getTime())) return String(v);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fmtDate = (v) => { if (!v) return '—'; const d = new Date(v); if (isNaN(d.getTime())) return String(v).slice(0, 10); const p = (n) => String(n).padStart(2, '0'); return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`; };

/* ─── current user ────────────────────────────────────────────────────── */
function useMe() {
  return useMemo(() => {
    const u = globals().getLogedInUser();
    const roles = (u?.data?.roles || u?.roles || []).map(r => String(r?.name || r?.role_name || r || '').toLowerCase());
    const role = String(u?.data?.role || u?.role || '').toLowerCase();
    const canManage = [...roles, role].some(r => ['admin', 'superadmin', 'super_admin', 'company_admin', 'menaxher', 'manager', 'hr', 'hr_manager'].includes(r));
    const name = [u?.data?.firstname, u?.data?.lastname].filter(Boolean).join(' ') || u?.data?.username || u?.data?.email || 'Ju';
    return { canManage, canViewSalary: canManage, name };
  }, []);
}

/* ═══ main ═══════════════════════════════════════════════════════════════ */
export default function EmploymentChanges() {
  const theme = useTheme();
  const me = useMe();

  const [rows, setRows] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ecRes, wRes, dRes, bRes, wtRes] = await Promise.all([
        authFetch('/api/employment-changes').then(r => r.json()).catch(() => ({})),
        apiService().get('selectworker'),
        apiService().get('department'),
        apiService().get('branch'),
        apiService().get('worktype'),
      ]);
      setRows(ecRes?.data || []);
      setWorkers(wRes?.data || []);
      setDepartments(dRes?.data || []);
      setBranches(bRes?.data || []);
      setWorkTypes((wtRes?.data || []).map((x, i) => ({ id: x.id ?? x.Id ?? i + 1, name: x.name ?? x.Name ?? x.WorkType ?? '' })).filter(x => x.name));
    } catch (e) { /* keep last */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = filter === 'all' ? rows : rows.filter(r => r.Status === filter);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(r =>
      (r.WorkerName || '').toLowerCase().includes(q) ||
      (r.SubmittedBy || '').toLowerCase().includes(q) ||
      (r.Items || []).some(it => (it.ProposedValue || '').toLowerCase().includes(q) || (it.FieldLabel || '').toLowerCase().includes(q))
    );
    return list;
  }, [rows, filter, search]);

  const stats = useMemo(() => ({
    total: rows.length,
    pending: rows.filter(r => ['pending', 'pending_supervisor', 'pending_hr', 'pending_management'].includes(r.Status)).length,
    scheduled: rows.filter(r => r.Status === 'scheduled').length,
    applied: rows.filter(r => r.Status === 'applied').length,
  }), [rows]);

  const summarize = (items) => {
    const parts = (items || []).map(it => `${it.FieldLabel}: ${it.OldValue || '—'} → ${it.ProposedValue}`);
    if (!parts.length) return '—';
    if (parts.length <= 2) return parts.join('  ·  ');
    return `${parts.slice(0, 2).join('  ·  ')}  ·  +${parts.length - 2} të tjera`;
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, py: 1.5 }}>
      <PageHeader
        title="Karriera & Kompensimi"
        subtitle="Kërkesa për ndryshime në të dhënat e punonjësit — pagë, pozitë, departament e të tjera"
        actions={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={{ fontWeight: 600, textTransform: 'none' }}>Kërkesë e re për ndryshim</Button>}
      />

      {/* KPI chips */}
      <Grid container spacing={1.25} sx={{ mb: 1.75 }}>
        {[
          { label: 'Gjithsej', value: stats.total, color: theme.palette.text.secondary },
          { label: 'Në pritje', value: stats.pending, color: STATUS.pending.color },
          { label: 'Planifikuar', value: stats.scheduled, color: STATUS.scheduled.color },
          { label: 'Zbatuar', value: stats.applied, color: STATUS.applied.color },
        ].map((c, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
              <Typography sx={{ fontSize: '.64rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>{c.label}</Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: c.color }}>{loading ? '—' : c.value}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
        <TextField size="small" placeholder="Kërko punonjës, ndryshim, parashtrues…" value={search} onChange={e => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 420 }} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }} />
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Statusi</InputLabel>
          <Select label="Statusi" value={filter} onChange={e => setFilter(e.target.value)}>
            <MenuItem value="all">Të gjitha</MenuItem>
            {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>

      {/* table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size="small" sx={{ '& td': { py: 1 } }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '.66rem', textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary', bgcolor: 'grey.50' } }}>
                <TableCell>Punonjësi</TableCell>
                <TableCell>Ndryshimet e kërkuara</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Data e hyrjes në fuqi</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Parashtruar nga</TableCell>
                <TableCell>Statusi</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Përditësuar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 5, color: 'text.disabled' }}>Asnjë kërkesë</TableCell></TableRow>
              )}
              {(rpp > 0 ? filtered.slice(page * rpp, page * rpp + rpp) : filtered).map((r) => {
                const st = STATUS[r.Status] || STATUS.pending;
                return (
                  <TableRow key={r.Id} hover onClick={() => setDetailId(r.Id)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, bgcolor: 'primary.50', color: 'primary.main' }}>
                          {(r.WorkerName || '?').split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
                        </Avatar>
                        <Typography sx={{ fontSize: '.83rem', fontWeight: 600 }}>{r.WorkerName || '—'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 380 }}>
                      <Typography sx={{ fontSize: '.78rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {summarize(r.Items)}
                        {r.HiddenCount > 0 && <Box component="span" sx={{ color: 'text.disabled' }}> · +{r.HiddenCount} (paga)</Box>}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, fontSize: '.78rem', whiteSpace: 'nowrap' }}>{fmtDate(r.EffectiveDate)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: '.78rem' }}>{r.SubmittedBy || '—'}{r.OnBehalfOf ? ' ·(në emër)' : ''}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip size="small" label={st.label} sx={{ height: 22, fontSize: '.68rem', fontWeight: 700, bgcolor: st.bg, color: st.color }} />
                        {r.CanDecide && <Chip size="small" label="Për ju" sx={{ height: 20, fontSize: '.62rem', fontWeight: 800, bgcolor: '#DCFCE7', color: '#15803D', border: '1px solid #86EFAC' }} />}
                        {r.HrSkipped && <Chip size="small" label="C-Level · pa HR" sx={{ height: 20, fontSize: '.62rem', fontWeight: 800, bgcolor: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D' }} />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: '.72rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                      {r.UpdatedBy || '—'}<br />{fmtDT(r.UpdatedAt)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rpp} rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }} />
      </Paper>

      {createOpen && (
        <CreateDialog
          open={createOpen} onClose={() => setCreateOpen(false)}
          workers={workers} departments={departments} branches={branches} workTypes={workTypes}
          me={me}
          onSaved={() => { setCreateOpen(false); load(); setSnack({ open: true, message: 'Kërkesa u parashtrua (Në pritje).', severity: 'success' }); }}
          onError={(m) => setSnack({ open: true, message: m, severity: 'error' })}
        />
      )}

      {detailId != null && (
        <DetailDialog
          id={detailId} onClose={() => setDetailId(null)} me={me}
          onChanged={(msg) => { load(); if (msg) setSnack({ open: true, message: msg, severity: 'success' }); }}
          onError={(m) => setSnack({ open: true, message: m, severity: 'error' })}
        />
      )}

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}

/* ═══ Create dialog — dynamic form ══════════════════════════════════════ */
function CreateDialog({ open, onClose, workers, departments, branches, workTypes, me, onSaved, onError }) {
  const theme = useTheme();
  const [workerId, setWorkerId] = useState('');
  const [selected, setSelected] = useState({});   // { key: { value, raw, label } }
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [modulePositions, setModulePositions] = useState([]);
  const [serverCurrentPos, setServerCurrentPos] = useState(null);
  // Vetëm informacion për parashtruesin; rrugën e vendos serveri në POST.
  const [serverCLevel, setServerCLevel] = useState(false);

  const worker = useMemo(() => workers.find(w => String(w.Id ?? w.id) === String(workerId)) || null, [workers, workerId]);

  // Position options as {id, name} — the ID is what the same-position check
  // runs on. The module-scoped endpoint is used rather than /api/positions,
  // which is admin+manager only and would leave HR with an empty dropdown.
  useEffect(() => {
    let alive = true;
    const qs = workerId ? `?workerId=${encodeURIComponent(workerId)}` : '';
    authFetch(`/api/employment-changes/position-options${qs}`)
      .then(r => r.json())
      .then(j => {
        if (!alive || !j?.data) return;
        setModulePositions((j.data.options || []).filter(o => o?.name));
        setServerCurrentPos(j.data.current || null);
        setServerCLevel(j.data.cLevel === true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [workerId]);

  // The worker payload already carries PositionId; the server's answer wins when
  // present because it also resolves an id for legacy name-only rows.
  const currentPosition = useMemo(
    () => (serverCurrentPos && (serverCurrentPos.id || serverCurrentPos.name)
      ? { id: serverCurrentPos.id, name: serverCurrentPos.name }
      : currentPositionOf(worker)),
    [serverCurrentPos, worker]
  );

  const positionOptions = useMemo(() => {
    const byKey = new Map();
    const add = (o) => {
      const name = String(o?.name || '').trim();
      if (!name) return;
      const key = o?.id != null ? `id:${o.id}` : `nm:${name.toLowerCase()}`;
      if (!byKey.has(key)) byKey.set(key, { id: o.id ?? null, name });
    };
    modulePositions.forEach(add);
    // Fallback so the list is never empty if the endpoint is unavailable.
    workers.forEach(w => add({ id: w.PositionId ?? w.position_id ?? null, name: w.Position }));
    return Array.from(byKey.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [modulePositions, workers]);

  // Requirement: the current position is excluded from the options entirely.
  const selectablePositionOptions = useMemo(
    () => selectablePositions(positionOptions, currentPosition),
    [positionOptions, currentPosition]
  );

  const toggleType = (key) => {
    const tp = typeOf(key);
    if (tp.manageOnly && !me.canManage) { onError('Nuk keni të drejtë të parashtroni ndryshim page.'); return; }
    setSelected(s => {
      const next = { ...s };
      if (next[key]) delete next[key]; else next[key] = { value: '', raw: '', label: key === 'other' ? '' : typeOf(key).label };
      return next;
    });
  };
  const setChange = (key, patch) => setSelected(s => ({ ...s, [key]: { ...s[key], ...patch } }));

  // Same-position check, by ID. Returns the message to show, or '' when fine.
  // Shared by validate() and the Save-button guard so they can never disagree.
  const positionError = useMemo(() => {
    const ch = selected.position;
    if (!ch) return '';
    const picked = { id: ch.raw ?? null, name: ch.value ?? '' };
    if (picked.id == null && !String(picked.name).trim()) return '';   // nothing chosen yet
    return isSamePosition(currentPosition, picked) ? POSITION_SAME_MESSAGE : '';
  }, [selected, currentPosition]);

  // Same-salary / invalid-amount check, numeric at the stored scale. Shared by
  // validate() and the Save-button guard so they can never disagree.
  const salaryError = useMemo(() => {
    const ch = selected.salary;
    if (!ch) return '';
    return salaryFieldError(ch.raw ?? ch.value, currentSalaryOf(worker));
  }, [selected, worker]);

  const validate = () => {
    const e = {};
    if (!workerId) e.worker = 'Zgjidhni punonjësin';
    const keys = Object.keys(selected);
    if (!keys.length) e.types = 'Zgjidhni së paku një ndryshim';
    keys.forEach(k => {
      const ch = selected[k]; const tp = typeOf(k);
      if (k === 'other' && !(ch.label || '').trim()) e[`${k}_label`] = 'Shkruani çka ndryshohet';
      if (!String(ch.value ?? '').trim() && !String(ch.raw ?? '').trim()) e[k] = 'Vlera e propozuar mungon';
      // frontend equality check for simple text/number
      if (tp.curCmp && String(ch.value ?? '').trim() !== '' && String(ch.value).trim() === String(tp.curCmp(worker)).trim()) {
        e[k] = 'Vlera e propozuar është e njëjtë me aktualen';
      }
    });
    if (positionError) e.position = positionError;
    if (salaryError) e.salary = salaryError;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const Items = Object.entries(selected).map(([key, ch]) => ({
        FieldKey: key,
        FieldLabel: key === 'other' ? (ch.label || 'Tjetër') : typeOf(key).label,
        ProposedValue: ch.value,
        ProposedRaw: ch.raw !== '' && ch.raw != null ? ch.raw : ch.value,
      }));
      const res = await authFetch('/api/employment-changes', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ WorkerId: workerId, EffectiveDate: effectiveDate || null, Reason: reason, Notes: notes, Items }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) onSaved();
      else onError(json.error || json.message || 'Gabim gjatë ruajtjes.');
    } catch (e) { onError('Gabim i papritur: ' + e.message); }
    finally { setSaving(false); }
  };

  const proposedField = (key) => {
    const ch = selected[key]; const tp = typeOf(key);
    const err = errors[key];
    const common = { size: 'small', fullWidth: true, error: !!err, helperText: err };
    switch (tp.input) {
      case 'number': {
        // Salary reports its own numeric/same-value error as the user types.
        const numErr = key === 'salary' ? (err || salaryError) : err;
        return <TextField {...common} type="number" label="E propozuar" value={ch.value}
          error={!!numErr}
          helperText={numErr || (key === 'salary' ? `Paga aktuale: ${worker?.BaseSalary || 0} €` : undefined)}
          onChange={e => setChange(key, { value: e.target.value, raw: e.target.value })}
          InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }} />;
      }
      case 'dept':
        return <FormControl size="small" fullWidth error={!!err}><InputLabel>E propozuar</InputLabel>
          <Select label="E propozuar" value={ch.raw || ''} onChange={e => { const d = departments.find(x => String(x.Id ?? x.id) === String(e.target.value)); setChange(key, { raw: e.target.value, value: d?.Name || d?.name || '' }); }}>
            {departments.map(d => <MenuItem key={d.Id ?? d.id} value={d.Id ?? d.id}>{d.Name || d.name}</MenuItem>)}
          </Select>{err && <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>{err}</Typography>}</FormControl>;
      case 'branch':
        return <FormControl size="small" fullWidth error={!!err}><InputLabel>E propozuar</InputLabel>
          <Select label="E propozuar" value={ch.raw || ''} onChange={e => { const b = branches.find(x => String(x.Id ?? x.id) === String(e.target.value)); setChange(key, { raw: e.target.value, value: b?.Name || b?.name || '' }); }}>
            {branches.map(b => <MenuItem key={b.Id ?? b.id} value={b.Id ?? b.id}>{b.Name || b.name}</MenuItem>)}
          </Select>{err && <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>{err}</Typography>}</FormControl>;
      case 'worktype':
        return <FormControl size="small" fullWidth error={!!err}><InputLabel>E propozuar</InputLabel>
          <Select label="E propozuar" value={ch.raw || ''} onChange={e => { const t = workTypes.find(x => String(x.id) === String(e.target.value)); setChange(key, { raw: e.target.value, value: t?.name || '' }); }}>
            {workTypes.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
          </Select>{err && <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>{err}</Typography>}</FormControl>;
      case 'position': {
        // Options exclude the worker's current position, so it cannot be picked.
        // positionError still guards the case where it slips in via stale state.
        const posErr = err || positionError;
        const picked = selectablePositionOptions.find(o =>
          (ch.raw != null && ch.raw !== '' && String(o.id) === String(ch.raw)) ||
          (o.id == null && String(o.name) === String(ch.value || ''))
        ) || null;
        return <Autocomplete size="small"
          options={selectablePositionOptions}
          value={picked}
          getOptionLabel={(o) => (typeof o === 'string' ? o : (o?.name || ''))}
          onChange={(_, v) => setChange(key, { raw: v?.id ?? '', value: v?.name || '' })}
          isOptionEqualToValue={(o, v) => (o?.id != null && v?.id != null ? String(o.id) === String(v.id) : o?.name === v?.name)}
          noOptionsText="Asnjë pozitë tjetër në listë"
          renderInput={(p) => <TextField {...p} label="E propozuar (nga lista)" error={!!posErr}
            helperText={posErr || `Pozita aktuale: ${currentPosition.name || '—'} — zgjidh një pozitë tjetër`} />} />;
      }
      case 'manager':
        return <Autocomplete size="small"
          options={workers.filter(w => String(w.Id ?? w.id) !== String(workerId))}
          getOptionLabel={(o) => nameOfWorker(o)}
          onChange={(_, v) => setChange(key, { raw: v ? (v.Id ?? v.id) : '', value: v ? nameOfWorker(v) : '' })}
          renderInput={(p) => <TextField {...p} label="E propozuar" error={!!err} helperText={err} />} />;
      case 'other':
        return <Stack spacing={1}>
          <TextField size="small" fullWidth label="Çka ndryshohet" value={ch.label} error={!!errors[`${key}_label`]} helperText={errors[`${key}_label`]} onChange={e => setChange(key, { label: e.target.value })} />
          <TextField {...common} label="E propozuar" value={ch.value} onChange={e => setChange(key, { value: e.target.value, raw: e.target.value })} />
        </Stack>;
      default:
        return <TextField {...common} label="E propozuar" value={ch.value} onChange={e => setChange(key, { value: e.target.value, raw: e.target.value })} />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Kërkesë e re për ndryshim</Typography>
          <Typography variant="caption" color="text.secondary">Parashtruesi: <b>{me.name}</b> · Statusi fillestar: Në pritje</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseRoundedIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        {/* Step 1 — worker (searchable) */}
        <Autocomplete
          size="small"
          options={workers}
          getOptionLabel={(w) => `${nameOfWorker(w)}${w.Position ? ` — ${w.Position}` : ''}`}
          value={workers.find(w => String(w.Id ?? w.id) === String(workerId)) || null}
          onChange={(_, v) => { setWorkerId(v ? String(v.Id ?? v.id) : ''); setSelected({}); }}
          isOptionEqualToValue={(o, v) => String(o.Id ?? o.id) === String(v.Id ?? v.id)}
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            if (!q) return opts;
            return opts.filter(w => `${nameOfWorker(w)} ${w.Position || ''}`.toLowerCase().includes(q));
          }}
          renderInput={(params) => <TextField {...params} label="Kërko dhe zgjidh punonjësin" error={!!errors.worker} helperText={errors.worker} />}
          sx={{ mb: 2 }}
        />

        {worker && (
          <>
            {/* Step 2 — what to change */}
            <Typography sx={{ fontWeight: 700, fontSize: '.9rem', mb: 1 }}>Çfarë dëshironi të ndryshoni?</Typography>
            {errors.types && <Alert severity="warning" sx={{ mb: 1, py: 0 }}>{errors.types}</Alert>}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {CHANGE_TYPES.filter(t => !(t.manageOnly && !me.canManage)).map(t => {
                const on = !!selected[t.key]; const Icon = t.icon;
                return (
                  <Grid item xs={6} sm={3} key={t.key}>
                    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: on ? 'primary.main' : 'divider', bgcolor: on ? alpha(theme.palette.primary.main, 0.06) : 'transparent' }}>
                      <CardActionArea onClick={() => toggleType(t.key)} sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Icon sx={{ fontSize: 22, color: on ? 'primary.main' : 'text.secondary' }} />
                        <Typography sx={{ fontSize: '.74rem', fontWeight: on ? 700 : 500, color: on ? 'primary.main' : 'text.primary' }}>{t.label}</Typography>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {serverCLevel && selected.salary && (
              <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }}>
                Punonjës <b>C-Level</b>: kërkesa për ndryshim page kalon <b>pa fazën e HR-së</b> — mbikëqyrësi (nëse ka) → menaxhmenti. Rruga ruhet te historiku i kërkesës.
              </Alert>
            )}

            {/* Step 3 — dynamic fields */}
            {Object.keys(selected).length > 0 && (
              <Stack spacing={1.25} sx={{ mb: 2 }}>
                {Object.keys(selected).map(key => {
                  const tp = typeOf(key);
                  return (
                    <Paper key={key} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '.82rem', mb: 1 }}>{key === 'other' ? (selected[key].label || 'Tjetër') : tp.label}</Typography>
                      <Grid container spacing={1.25} alignItems="flex-start">
                        <Grid item xs={12} sm={5}>
                          <TextField size="small" fullWidth label="Aktuale" value={tp.cur(worker)} InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }} />
                        </Grid>
                        <Grid item xs={12} sm={7}>{proposedField(key)}</Grid>
                      </Grid>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {/* Step 4 — general */}
            <Divider sx={{ mb: 1.5 }} />
            <Grid container spacing={1.25}>
              <Grid item xs={12} sm={4}>
                <TextField size="small" fullWidth type="date" label="Data e hyrjes në fuqi" InputLabelProps={{ shrink: true }} value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField size="small" fullWidth label="Arsyeja e ndryshimit" value={reason} onChange={e => setReason(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth multiline minRows={2} label="Shënime shtesë" value={notes} onChange={e => setNotes(e.target.value)} />
              </Grid>
            </Grid>

            {/* summary */}
            {Object.keys(selected).length > 0 && (
              <Alert severity="info" icon={false} sx={{ mt: 2, borderRadius: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '.8rem', mb: 0.5 }}>Përmbledhje e ndryshimeve</Typography>
                {Object.keys(selected).map(key => {
                  const tp = typeOf(key); const ch = selected[key];
                  return <Typography key={key} sx={{ fontSize: '.78rem' }}>• {key === 'other' ? (ch.label || 'Tjetër') : tp.label}: <b>{tp.cur(worker)}</b> → <b style={{ color: theme.palette.success.main }}>{ch.value || '—'}{tp.unit ? ` ${tp.unit}` : ''}</b></Typography>;
                })}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        {/* Saving stays blocked until a DIFFERENT position / salary is entered. */}
        {(positionError || salaryError) && <Typography variant="caption" color="error" sx={{ flex: 1, ml: 1 }}>{positionError || salaryError}</Typography>}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button variant="contained" onClick={submit} disabled={saving || !worker || !Object.keys(selected).length || !!positionError || !!salaryError} sx={{ textTransform: 'none', fontWeight: 600 }}>
          {saving ? 'Duke parashtruar…' : 'Parashtro kërkesën'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ═══ Approval chain — Parashtruar → Mbikëqyrësi → HR → Zbatuar ══════════ */
const CHAIN_C = {
  done:      { c: '#15803D', bg: '#DCFCE7' },
  current:   { c: '#B45309', bg: '#FEF3C7' },
  rejected:  { c: '#B91C1C', bg: '#FEE2E2' },
  pending:   { c: '#94A3B8', bg: '#F1F5F9' },
  scheduled: { c: '#0369A1', bg: '#F0F9FF' },
  review:    { c: '#C2410C', bg: '#FFEDD5' },
  skip:      { c: '#CBD5E1', bg: '#F8FAFC' },
  // HR e anashkaluar me vendim (C-Level + pagë): dallohet nga `skip` i thjeshtë,
  // që lexuesi ta shohë se hapi u kapërcye me qëllim, jo se s'ekzistonte.
  skipped:   { c: '#92400E', bg: '#FEF3C7' },
};

// Eksportohet për testin e gjendjes: zinxhiri duhet të tregojë hapin e HR-së si
// të anashkaluar kur serveri e ngriu rrugën ashtu (`HrSkipped`).
export function ApprovalChain({ data }) {
  const s = data.Status;
  const hasSup = !!data.SupervisorWorkerId;

  const supState = !hasSup ? 'skip'
    : (s === 'rejected' && data.SupervisorDecisionBy && !data.HrDecisionBy) ? 'rejected'
    : data.SupervisorDecisionAt ? 'done'
    : s === 'pending_supervisor' ? 'current'
    : 'pending';

  const hrState = data.HrSkipped ? 'skipped'
    : (s === 'rejected' && data.HrDecisionBy && !data.ManagementDecisionBy) ? 'rejected'
    : data.HrDecisionAt ? 'done'
    : (s === 'pending_hr' || s === 'pending') ? 'current'
    : (s === 'pending_supervisor') ? 'pending'
    : (s === 'cancelled') ? 'skip'
    : 'pending';

  const mgmtState = (s === 'rejected' && data.ManagementDecisionBy) ? 'rejected'
    : data.ManagementDecisionAt ? 'done'
    : (s === 'pending_management') ? 'current'
    : (s === 'pending_supervisor' || s === 'pending_hr' || s === 'pending') ? 'pending'
    : (s === 'rejected' || s === 'cancelled') ? 'skip'
    : 'pending';

  const finState = s === 'applied' ? 'done'
    : s === 'scheduled' ? 'scheduled'
    : s === 'needs_review' ? 'review'
    : (s === 'rejected' || s === 'cancelled') ? 'skip'
    : 'pending';

  const finLabel = s === 'applied' ? 'Zbatuar' : s === 'scheduled' ? 'Planifikuar' : s === 'needs_review' ? 'Kërkon rishikim' : s === 'cancelled' ? 'Anuluar' : 'Zbatim';

  const steps = [
    { key: 'sub', icon: PersonAddAltRoundedIcon, title: 'Parashtruar', state: 'done', who: data.SubmittedBy, when: data.SubmittedAt },
    ...(hasSup ? [{ key: 'sup', icon: HowToRegRoundedIcon, title: 'Mbikëqyrësi', state: supState, who: data.SupervisorDecisionBy, when: data.SupervisorDecisionAt }] : []),
    { key: 'hr', icon: GavelRoundedIcon, title: data.HrSkipped ? 'HR — anashkaluar' : 'HR', state: hrState, who: data.HrDecisionBy, when: data.HrDecisionAt },
    { key: 'mgmt', icon: AccountBalanceRoundedIcon, title: 'Menaxhmenti', state: mgmtState, who: data.ManagementDecisionBy, when: data.ManagementDecisionAt },
    { key: 'fin', icon: TaskAltRoundedIcon, title: finLabel, state: finState, who: data.AppliedBy, when: data.AppliedAt },
  ];

  const subText = (st, who, when) => {
    if (st === 'current') return 'Në pritje…';
    if (st === 'skipped') return 'C-Level · pagë';
    if (st === 'pending' || st === 'skip') return '—';
    if (st === 'rejected') return `Refuzuar${who ? ' · ' + who : ''}`;
    if (who || when) return `${who || ''}${who && when ? ' · ' : ''}${when ? fmtDT(when) : ''}`;
    return '—';
  };

  return (
    <Box sx={{ mb: 2, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', alignItems: 'flex-start', gap: 0.5, overflowX: 'auto' }}>
      {steps.map((step, i) => {
        const col = CHAIN_C[step.state] || CHAIN_C.pending;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.key}>
            {i > 0 && <Box sx={{ flex: '0 0 24px', height: 2, borderRadius: 1, mt: 2.5, bgcolor: (steps[i - 1].state === 'done' || (steps[i - 1].state === 'skipped' && steps[i - 2]?.state === 'done')) ? CHAIN_C.done.c : '#E2E8F0' }} />}
            <Box sx={{ flex: '1 1 0', minWidth: 96, textAlign: 'center' }}>
              <Box sx={{ width: 40, height: 40, mx: 'auto', borderRadius: '50%', bgcolor: col.bg, color: col.c, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1.5px solid ${col.c}` }}>
                <Icon sx={{ fontSize: 22 }} />
              </Box>
              <Typography sx={{ fontSize: '.68rem', fontWeight: 800, mt: 0.5, color: col.c, textTransform: 'uppercase', letterSpacing: '.02em' }}>{step.title}</Typography>
              <Typography sx={{ fontSize: '.64rem', color: 'text.secondary', lineHeight: 1.25, mt: 0.15 }} noWrap>{subText(step.state, step.who, step.when)}</Typography>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}

/* ═══ Detail dialog — workflow + history ════════════════════════════════ */
function DetailDialog({ id, onClose, me, onChanged, onError }) {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState(null); // { action, title, verb, color, required }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/employment-changes/${id}`);
      const json = await res.json().catch(() => ({}));
      setData(json?.data || null);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  const act = async (action, note) => {
    setBusy(true);
    try {
      const res = await authFetch(`/api/employment-changes/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: note || null }) });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        const labels = { approve: 'Kërkesa u aprovua', reject: 'Kërkesa u refuzua', cancel: 'Kërkesa u anulua', apply: 'Kërkesa u zbatua' };
        let msg = labels[action] || 'U përditësua';
        if (json.data?.status === 'needs_review') msg = 'Profili ka ndryshuar — kërkesa kaloi në "Kërkon rishikim".';
        onChanged(msg); reload();
        return true;
      }
      onError(json.error || json.message || 'Veprimi dështoi.');
      return false;
    } catch (e) { onError('Gabim: ' + e.message); return false; }
    finally { setBusy(false); }
  };
  const runPrompt = async (note) => { const okr = await act(prompt.action, note); if (okr) setPrompt(null); };

  const st = data ? (STATUS[data.Status] || STATUS.pending) : STATUS.pending;
  const isPending = data && ['pending', 'pending_supervisor', 'pending_hr', 'pending_management'].includes(data.Status);
  const canDecide = !!(data && data.CanDecide); // stage-aware, from backend
  const canApply  = me.canManage && data && ['approved', 'scheduled', 'needs_review'].includes(data.Status);
  const canCancel = me.canManage && data && !['applied', 'cancelled', 'rejected'].includes(data.Status);
  // Etiketa pasqyron rrugën e ngrirë nga serveri (`HrSkipped`): te C-Level me
  // pagë, mbikëqyrësi e dërgon drejt te menaxhmenti.
  const approveLabel = data?.Stage === 'supervisor' ? (data?.HrSkipped ? 'Aprovo & dërgo te Menaxhmenti' : 'Aprovo & dërgo te HR')
    : data?.Stage === 'hr' ? 'Aprovo & dërgo te Menaxhmenti'
    : 'Aprovo (final)';
  const approveTitle = data?.Stage === 'supervisor' ? 'Aprovim i mbikëqyrësit'
    : data?.Stage === 'hr' ? 'Aprovim i HR'
    : 'Aprovim final (Menaxhmenti)';

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}>
      {loading || !data ? (
        <Box sx={{ p: 3 }}><Skeleton height={30} width="50%" /><Skeleton height={20} /><Skeleton height={120} /></Box>
      ) : (
        <>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{data.WorkerName}</Typography>
                <Chip size="small" label={st.label} sx={{ height: 22, fontSize: '.68rem', fontWeight: 700, bgcolor: st.bg, color: st.color }} />
              </Stack>
              <Typography variant="caption" color="text.secondary">Kërkesë #{data.Id} · Data e hyrjes në fuqi: {fmtDate(data.EffectiveDate)}</Typography>
            </Box>
            <IconButton size="small" onClick={onClose}><CloseRoundedIcon /></IconButton>
          </Box>

          <DialogContent sx={{ p: 2 }}>
            {data.Status === 'needs_review' && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>Profili i punonjësit ka ndryshuar që nga parashtrimi. Verifiko vlerat para zbatimit.</Alert>}

            {/* approval chain */}
            <ApprovalChain data={data} />

            {isPending && canDecide && (
              <Alert severity="info" icon={<HowToRegRoundedIcon fontSize="inherit" />} sx={{ mb: 2, borderRadius: 2, py: 0.5 }}>
                {data.Stage === 'supervisor'
                  ? (data.HrSkipped
                    ? 'Ju jeni mbikëqyrësi i këtij punonjësi — punonjës C-Level: aprovimi juaj e dërgon kërkesën drejt te menaxhmenti (HR anashkalohet).'
                    : 'Ju jeni mbikëqyrësi i këtij punonjësi — aprovimi juaj e dërgon kërkesën te HR.')
                  : data.Stage === 'hr'
                  ? 'Aprovimi juaj (HR) e dërgon kërkesën te menaxhmenti për vendimin final.'
                  : 'Aprovimi juaj (Menaxhmenti) e finalizon kërkesën dhe e zbaton/planifikon ndryshimin.'}
              </Alert>
            )}
            {isPending && !canDecide && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2, py: 0.5 }}>
                Në pritje të vendimit — faza aktuale: <b>{data.StageLabel || (STATUS[data.Status]?.label)}</b>.
              </Alert>
            )}

            {/* changes table */}
            <Typography sx={{ fontWeight: 700, fontSize: '.85rem', mb: 1 }}>Ndryshimet</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <Table size="small">
                <TableHead><TableRow sx={{ '& th': { fontWeight: 700, fontSize: '.66rem', textTransform: 'uppercase', color: 'text.secondary' } }}>
                  <TableCell>Fusha</TableCell><TableCell>Aktuale (në parashtrim)</TableCell><TableCell>E propozuar</TableCell><TableCell>E zbatuar</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.Items.map(it => (
                    <TableRow key={it.Id}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '.8rem' }}>{it.FieldLabel}</TableCell>
                      <TableCell sx={{ fontSize: '.8rem', color: 'text.secondary' }}>{it.OldValue || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '.8rem', fontWeight: 600 }}><Stack direction="row" spacing={0.5} alignItems="center"><ArrowForwardIcon sx={{ fontSize: 13, color: 'text.disabled' }} />{it.ProposedValue}</Stack></TableCell>
                      <TableCell sx={{ fontSize: '.8rem', color: it.AppliedValue ? 'success.main' : 'text.disabled', fontWeight: it.AppliedValue ? 600 : 400 }}>{it.AppliedValue || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {(data.Reason || data.Notes) && (
              <Box sx={{ mb: 2 }}>
                {data.Reason && <Typography sx={{ fontSize: '.82rem' }}><b>Arsyeja:</b> {data.Reason}</Typography>}
                {data.Notes && <Typography sx={{ fontSize: '.82rem', color: 'text.secondary' }}><b>Shënime:</b> {data.Notes}</Typography>}
              </Box>
            )}

            {/* people / workflow meta */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {[
                ['Parashtruar nga', `${data.SubmittedBy || '—'}${data.OnBehalfOf ? ' (në emër)' : ''}`, fmtDT(data.SubmittedAt), ''],
                ['Aprovimi i mbikëqyrësit', data.SupervisorDecisionBy || (data.SupervisorWorkerId ? 'Në pritje' : 'Nuk kërkohet'), data.SupervisorDecisionAt ? fmtDT(data.SupervisorDecisionAt) : '', data.SupervisorDecisionNote],
                ['Aprovimi i HR', data.HrSkipped ? 'Anashkaluar — punonjës C-Level (ndryshim page)' : (data.HrDecisionBy || 'Në pritje'), data.HrDecisionAt ? fmtDT(data.HrDecisionAt) : '', data.HrSkipped ? `Rruga: ${data.ApprovalPath || '—'}` : data.HrDecisionNote],
                ['Vendimi i Menaxhmentit', data.ManagementDecisionBy || 'Në pritje', data.ManagementDecisionAt ? fmtDT(data.ManagementDecisionAt) : '', data.ManagementDecisionNote || data.DecisionNote],
                ['Zbatuar nga', data.AppliedBy || '—', data.AppliedAt ? fmtDT(data.AppliedAt) : '', ''],
              ].map(([lab, who, when, note], i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Paper variant="outlined" sx={{ p: 1, borderRadius: 1.5, height: '100%' }}>
                    <Typography sx={{ fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>{lab}</Typography>
                    <Typography sx={{ fontSize: '.82rem', fontWeight: 600 }}>{who}</Typography>
                    {when && <Typography sx={{ fontSize: '.68rem', color: 'text.secondary' }}>{when}</Typography>}
                    {note && <Typography sx={{ fontSize: '.72rem', color: 'text.primary', mt: 0.5, fontStyle: 'italic' }}>“{note}”</Typography>}
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* history */}
            {data.History?.length > 0 && (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: '.85rem', mb: 1 }}>Historiku i veprimeve</Typography>
                <Stack spacing={0.5}>
                  {data.History.map((h, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ fontSize: '.75rem' }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '.75rem', fontWeight: 600 }}>{ACTION_LABELS[h.Action] || h.Action}</Typography>
                      {h.Action === 'SKIP_HR' && <Chip size="small" label="C-Level" sx={{ height: 16, fontSize: '.58rem', fontWeight: 800, bgcolor: '#FEF3C7', color: '#92400E' }} />}
                      <Typography sx={{ fontSize: '.72rem', color: 'text.secondary' }}>{h.UserEmail}</Typography>
                      <Typography sx={{ fontSize: '.72rem', color: 'text.disabled', ml: 'auto' }}>{fmtDT(h.CreatedAt)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
            {busy && <LinearProgress sx={{ width: '100%' }} />}
            {canDecide && <Button color="error" startIcon={<CancelRoundedIcon />} onClick={() => setPrompt({ action: 'reject', title: `Refuzo — faza ${data.StageLabel || ''}`.trim(), verb: 'Refuzo', color: 'error', required: true })} disabled={busy} sx={{ textTransform: 'none' }}>Refuzo</Button>}
            {canCancel && <Button color="inherit" startIcon={<BlockRoundedIcon />} onClick={() => setPrompt({ action: 'cancel', title: 'Anulo kërkesën', verb: 'Anulo', color: 'inherit', required: false })} disabled={busy} sx={{ textTransform: 'none' }}>Anulo</Button>}
            <Box sx={{ flex: 1 }} />
            {canDecide && <Button variant="contained" color="success" startIcon={<CheckCircleRoundedIcon />} onClick={() => setPrompt({ action: 'approve', title: approveTitle, verb: approveLabel, color: 'success', required: true })} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600 }}>{approveLabel}</Button>}
            {canApply && <Button variant="contained" startIcon={<PlayCircleRoundedIcon />} onClick={() => act('apply')} disabled={busy} sx={{ textTransform: 'none', fontWeight: 600 }}>Zbato tani</Button>}
            {!canDecide && !canApply && !canCancel && <Typography variant="caption" color="text.secondary">S'ka veprim në dispozicion për ju në këtë fazë.</Typography>}
          </DialogActions>

          {prompt && <ReasonDialog prompt={prompt} busy={busy} onCancel={() => setPrompt(null)} onSubmit={runPrompt} />}
        </>
      )}
    </Dialog>
  );
}

/* ═══ Reason dialog — captures the justification for a decision ══════════ */
function ReasonDialog({ prompt, busy, onCancel, onSubmit }) {
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const missing = prompt.required && !note.trim();
  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>{prompt.title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {prompt.required ? 'Arsyetimi është i detyrueshëm dhe ruhet në historik.' : 'Arsyetimi (opsional) ruhet në historik.'}
        </Typography>
      </Box>
      <DialogContent sx={{ pt: 0.5 }}>
        <TextField
          autoFocus multiline minRows={3} fullWidth size="small"
          label={prompt.required ? 'Arsyetimi *' : 'Arsyetimi'}
          value={note}
          onChange={e => setNote(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && missing}
          helperText={touched && missing ? 'Ju lutem shkruani arsyetimin.' : ' '}
          placeholder="Shkruani arsyen e vendimit…"
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button color="inherit" onClick={onCancel} disabled={busy} sx={{ textTransform: 'none' }}>Mbyll</Button>
        <Button variant="contained" color={prompt.color === 'inherit' ? 'primary' : prompt.color} disabled={busy || missing}
          onClick={() => { setTouched(true); if (!missing) onSubmit(note.trim()); }}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          {prompt.verb}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
